import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, MinusCircle, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Skip Today / Don't-Call panel.
 *
 * Rendered as a lower-visual-weight section beneath the priority feed so it
 * never competes with it — muted greys, thin borders, collapsible, and a
 * clear kicker that explains what the section is for.
 *
 * Each bucket shows an aggregated reason ("Contacted in the last 24 hours")
 * plus the specific names/counts underneath so the agent can scan and move on.
 */
const SkipTodayPanel = ({ data, loading, onSnoozed }) => {
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState('');
  const buckets = data?.buckets || [];
  const total = data?.total_deprioritized || 0;

  // Aggregate buckets (e.g. auto_reminder_queued rolls up N renewals into one
  // headline) have no individual entity_id and therefore can't be snoozed.
  const isSnoozable = (bucketKey, item) => Boolean(item.lead_id) && bucketKey !== 'auto_reminder_queued';

  const snooze = async (entityId, days) => {
    setBusyId(`${entityId}-${days}`);
    try {
      await axios.post(
        `${API}/ai/skip-today/snooze`,
        { entity_id: entityId, days },
        { withCredentials: true },
      );
      toast.success(`Snoozed for ${days} day${days === 1 ? '' : 's'} — they'll reappear in the skip list after that.`);
      if (onSnoozed) onSnoozed();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not snooze — please retry');
    } finally { setBusyId(''); }
  };

  // Hide the section entirely when there's genuinely nothing to skip so it
  // doesn't add visual noise on a fresh account.
  if (!loading && total === 0) return null;

  return (
    <div
      className="rounded-xl border border-slate-200 bg-slate-50/60 mt-2"
      data-testid="skip-today-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left"
        data-testid="skip-today-toggle"
      >
        <div className="flex items-center gap-2">
          <MinusCircle className="h-4 w-4 text-slate-400" />
          <span className="text-sm font-medium text-slate-700" style={{ fontFamily: 'Manrope' }}>
            Skip today
          </span>
          <span className="text-[11px] text-slate-500">
            — {total} contact{total === 1 ? '' : 's'} to deprioritize · low priority section
          </span>
        </div>
        {open
          ? <ChevronUp className="h-4 w-4 text-slate-400" />
          : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>

      {open && (
        <div className="px-4 pb-3 pt-1" data-testid="skip-today-body">
          {loading ? (
            <div className="animate-pulse space-y-2 py-2">
              <div className="h-3 bg-slate-200 rounded w-10/12" />
              <div className="h-3 bg-slate-200 rounded w-8/12" />
              <div className="h-3 bg-slate-200 rounded w-9/12" />
            </div>
          ) : (
            <>
              <p className="text-[11px] text-slate-500 mb-2 italic">
                These contacts are covered — automated reminders, recent touches, or scheduled follow-ups
                already handle them. Focus on the Priority Feed above instead.
              </p>
              <ul className="space-y-2" data-testid="skip-today-buckets">
                {buckets.map((b) => (
                  <li
                    key={b.key}
                    className="border-l-2 border-slate-300 bg-white/60 pl-3 py-2 rounded-r"
                    data-testid={`skip-bucket-${b.key}`}
                  >
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      {b.label}
                    </p>
                    <ul className="mt-1 space-y-1">
                      {b.items.map((it, i) => {
                        const eid = it.lead_id;
                        const canSnooze = isSnoozable(b.key, it);
                        return (
                          <li key={i} className="text-[13px] text-slate-700 leading-snug flex items-start justify-between gap-2 group">
                            <div className="flex-1 min-w-0">
                              <span className="font-medium">{it.name}</span>
                              <span className="text-slate-500"> — {it.reason}</span>
                            </div>
                            {canSnooze && (
                              <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`skip-snooze-row-${eid}`}>
                                <button
                                  type="button"
                                  onClick={() => snooze(eid, 3)}
                                  disabled={busyId === `${eid}-3`}
                                  className="text-[10px] px-1.5 py-0.5 rounded border border-slate-300 text-slate-600 hover:bg-slate-100 hover:text-slate-800 flex items-center gap-0.5 disabled:opacity-50"
                                  data-testid={`skip-snooze-3d-${eid}`}
                                  title="Un-skip for 3 days — they'll reappear here after that."
                                >
                                  <Clock className="h-2.5 w-2.5" />
                                  3d
                                </button>
                                <button
                                  type="button"
                                  onClick={() => snooze(eid, 7)}
                                  disabled={busyId === `${eid}-7`}
                                  className="text-[10px] px-1.5 py-0.5 rounded border border-slate-300 text-slate-600 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-50"
                                  data-testid={`skip-snooze-7d-${eid}`}
                                  title="Un-skip for 7 days"
                                >
                                  7d
                                </button>
                              </div>
                            )}
                          </li>
                        );
                      })}
                      {b.extra_count > 0 && (
                        <li className="text-[11px] text-slate-400 italic">
                          + {b.extra_count} more
                        </li>
                      )}
                    </ul>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SkipTodayPanel;
